/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChartPie,
  HiOutlineFlag,
  HiOutlineGlobeAlt,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";

import ListPage from "../../../layout/ListPage";
import type { Action, Column } from "../../../ui";
import { useContinentRegionContext } from "../../../context/ContinentRegionContext";
import { addOneRegion } from "../../../lib/breadCrumb/BreadCrumb";
import {
  pushCurrentRegion,
  pushRegion,
} from "../../../lib/sysNavigation/SysNavigation";

type RegionRow = {
  continent_region_id: number | string;
  name?: string;
  slug?: string;
  country_number?: number;
  [key: string]: unknown;
};

const addRoute = "/region/add";
const editRoute = "/region/edit";
const dashboardRoute = "/region/dashboard";
const countriesRoute = "/countries";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { regions, fetchRegions, deleteRegion, updateRegionState } =
    useContinentRegionContext() as any;

  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!regions) fetchRegions();
  }, [regions]);

  const rows = useMemo<RegionRow[] | null>(() => {
    if (!Array.isArray(regions)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return regions as RegionRow[];
    return (regions as RegionRow[]).filter((r) =>
      String(r.name ?? "").toLowerCase().includes(q),
    );
  }, [regions, query]);

  const handleDelete = async (row: RegionRow) => {
    const { status } = await deleteRegion(row.continent_region_id);
    if (status === 200 && Array.isArray(regions)) {
      updateRegionState(
        (regions as RegionRow[]).filter(
          (r) => r.continent_region_id !== row.continent_region_id,
        ),
      );
    }
  };

  const columns: Column<RegionRow>[] = [
    {
      header: "Region",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineGlobeAlt className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">
              {row.name ?? "—"}
            </p>
            <p className="truncate text-[11px] text-gray-500">
              {row.slug ?? ""}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: "Countries",
      cell: (row) => (
        <span className="inline-flex items-center rounded-full bg-brand/5 px-2.5 py-1 text-[11px] font-medium text-brand ring-1 ring-inset ring-brand/15">
          {row.country_number ?? 0}
        </span>
      ),
    },
  ];

  const rowActions = (row: RegionRow): Action[] => [
    {
      label: "Dashboard",
      icon: <HiOutlineChartPie className="h-3.5 w-3.5" />,
      onClick: () => {
        pushCurrentRegion(row);
        navigate(dashboardRoute);
      },
    },
    {
      label: "Countries",
      icon: <HiOutlineFlag className="h-3.5 w-3.5" />,
      onClick: () => {
        addOneRegion({
          id: row.continent_region_id,
          name: row.name,
          route: "/region/manage",
          data: row,
        });
        pushRegion(row);
        navigate(countriesRoute);
      },
    },
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushCurrentRegion(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name ?? "this region"}?`,
        description: "Countries inside this region keep their data.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<RegionRow>
      title="Regions"
      subtitle="Regional groupings across Africa."
      icon={<HiOutlineGlobeAlt className="h-5 w-5" />}
      actions={[{ label: "Add region", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.continent_region_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search regions…",
      }}
      empty="No regions yet."
    />
  );
};

export default Overview;
